import groupBy from 'lodash.groupby';
import { getCurrentUser } from 'modules/data';
import { useProject } from 'modules/routes';
import { useSelector } from 'react-redux';

import UserProject from './UserProject';

/**
 * Groups account access information by project, showing the current project first.
 * Each row loads its own project display data through Redux.
 *
 * @returns {React.ReactElement} Account project rows.
 */
function UserProjects() {
    const user = useSelector(getCurrentUser);
    const { project: currentProject } = useProject();
    const userProjects = Object.values(user?.user_projects || {});

    const currentUserProject = userProjects.find(
        (urp) => urp.project_id === currentProject?.id
    );

    const roles = user?.user_roles && Object.values(user.user_roles);
    const groupedRoles = groupBy(roles, 'project_id');

    const interviewPermissions =
        user?.interview_permissions &&
        Object.values(user.interview_permissions);
    const groupedInterviewPermissions = groupBy(
        interviewPermissions,
        'project_id'
    );

    const tasks = user?.tasks && Object.values(user.tasks);
    const groupedTasks = groupBy(tasks, 'project_id');

    const supervisedTasks =
        user?.supervisedTasks && Object.values(user.supervised_tasks);
    const groupedSupervisedTasks = groupBy(supervisedTasks, 'project_id');

    return (
        <>
            {/* Show current project first for stable ordering on account page. */}
            {currentProject && currentUserProject && (
                <UserProject
                    key={currentProject.id}
                    userProject={currentUserProject}
                    roles={groupedRoles[currentProject.id]}
                    interviewPermissions={
                        groupedInterviewPermissions[currentProject.id]
                    }
                    tasks={groupedTasks[currentProject.id]}
                    supervisedTasks={groupedSupervisedTasks[currentProject.id]}
                />
            )}
            {/* Render remaining project memberships after the current project. */}
            {userProjects.map((urp) => {
                if (urp.project_id !== currentProject?.id) {
                    return (
                        <UserProject
                            key={urp.id}
                            userProject={urp}
                            roles={groupedRoles[urp.project_id]}
                            interviewPermissions={
                                groupedInterviewPermissions[urp.project_id]
                            }
                            tasks={groupedTasks[urp.project_id]}
                            supervisedTasks={
                                groupedSupervisedTasks[urp.project_id]
                            }
                        />
                    );
                }
            })}
        </>
    );
}

export default UserProjects;
